import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form from "./base"
import Calc from "@/common/util/calculator"
import pathRounding from "@/signomatic/util/path_rounding"

export default class CurvedTop extends Form {
  protected path(width: number, height: number, rounding?: number) {
    const curveH = height * 0.4
    let cmds: string[] = []

    if (rounding) {
      const roundDiff = rounding / 2

      cmds = [this.pathMoveTo(-(width / 2), height / 2)] // LL
      cmds.push(this.pathLineToAbs(-(width / 2), -(height / 2) + roundDiff)) // UL
      cmds.push(
        this.pathCurveAbs(
          -(width / 2),
          -(height / 2) + roundDiff / 3,
          -(width / 2) + roundDiff / 3,
          -(height / 2),
          -(width / 2) + roundDiff,
          -(height / 2)
        )
      ) // UL rounding

      // rounding magic happening here
      const curveDiffX = rounding / 2
      let curveDiffY = rounding / 2
      let roundness = 0.35

      if (height > width) {
        curveDiffY = rounding * 2
        roundness = 0.7
      } else if (height == width) {
        curveDiffY = rounding
        roundness = 0.15
      }

      cmds.push(
        this.pathCurveAbs(
          -(width / 2) + width * 0.4 + roundDiff,
          -(height / 2) + height * 0.02,
          -(width / 2) + width * 0.78,
          -(height / 2) + height * 0.1,
          width / 2 - curveDiffX,
          -(height / 2) + curveH - curveDiffY
        )
      ) // UC
      cmds.push(
        this.pathCurveAbs(
          width / 2 - curveDiffX,
          -(height / 2) + curveH - curveDiffY,
          width / 2,
          -(height / 2) + curveH - curveDiffY * roundness,
          width / 2,
          -(height / 2) + curveH + curveDiffY
        )
      ) // UR rounding

      cmds.push(this.pathLineToAbs(width / 2, height / 2)) // LR
    } else {
      const [side, rest] = this.snappableCommands(width, height)
      rest.shift()
      cmds = side.concat(rest)
    }

    cmds.push(this.pathClose())

    let path = cmds.join(" ")

    if (rounding) {
      path = pathRounding(path, rounding).join("")
    }

    return new PathObject(path, this.defaultParams)
  }

  protected snappablePaths(width: number, height: number): [DrawableMixin, DrawableMixin] {
    const [side, rest] = this.snappableCommands(width, height)

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  private snappableCommands(width: number, height: number): [string[], string[]] {
    const curveH = height * 0.4
    const side = [this.pathMoveTo(-(width / 2), height / 2)] // LL

    side.push(this.pathLineToAbs(-(width / 2), -(height / 2))) // UL

    const rest = [this.pathMoveTo(-(width / 2), -(height / 2))] // UL
    rest.push(
      this.pathCurveAbs(
        -(width / 2) + width * 0.4,
        -(height / 2) + height * 0.02,
        -(width / 2) + width * 0.78,
        -(height / 2) + height * 0.1,
        width / 2,
        -(height / 2) + curveH
      )
    ) // UC
    rest.push(this.pathLineToAbs(width / 2, height / 2)) // LR
    rest.push(this.pathLineToAbs(-(width / 2), height / 2)) // LL

    return [side, rest]
  }

  protected get cutlineRounding() {
    return this.printCutlineRounding(this.calcRounding())
  }

  private calcRounding() {
    if (["aluminium_sign", "acryl_sign", "acryl_alu_sign"].includes(this.signType)) {
      return Calc.mmToPx(1)
    }

    return 0
  }
}
